import type { Action } from 'state/actions';
import { addKey, addOrReplaceKey, patchKey, patchKeyIfExist, removeKey } from 'state/utils/collection';

import type { PriceImprovementRequest, PriceImprovementRequestsMap } from './priceImprovementRequest.model';

export function priceImprovementRequestsReducer(
  state: PriceImprovementRequestsMap = {},
  action: Action,
): PriceImprovementRequestsMap {
  switch (action.type) {
    case 'PRICE_IMPROVEMENT_INITIALIZED':
      return addKey(state, action.quoteId, <PriceImprovementRequest>{
        status: 'Initialized',
        expirationTime: 'ThreeMinutes',
      });
    case 'PRICE_IMPROVEMENT_EXPANDED':
      return patchKey(state, action.quoteId, _ => ({
        isExpanded: action.isExpanded,
      }));
    case 'PRICE_IMPROVEMENT_COMMENT_CHANGED':
      return patchKey(state, action.quoteId, _ => ({
        comment: action.comment,
      }));
    case 'PRICE_IMPROVEMENT_REQUESTED_TRADING_OFFER_CHANGED':
      return patchKey(state, action.quoteId, _ => ({
        requestedTradingOffer: action.requestedTradingOffer,
      }));
    case 'PRICE_IMPROVEMENT_EXPIRATION_TIME_CHANGED':
      return patchKey(state, action.quoteId, _ => ({
        expirationTime: action.expirationTime,
      }));
    case 'PRICE_IMPROVEMENT_CREATE_REQUESTED':
      return patchKey(state, action.quoteId, _ => ({
        status: 'Creating' as const,
        creationError: undefined,
      }));
    case 'PRICE_IMPROVEMENT_CREATE_RECEIVED':
      return patchKey(state, action.quoteId, _ => ({
        status: 'Created' as const,
        requestForPriceImprovementId: action.requestForPriceImprovementId,
      }));
    case 'PRICE_IMPROVEMENT_CREATE_FAILED':
      return patchKey(state, action.quoteId, _ => ({
        status: 'Initialized' as const,
        creationError: action.error,
      }));
    case 'PRICE_IMPROVEMENT_CANCEL_REQUESTED':
      return patchKey(state, action.quoteId, _ => ({
        status: 'Cancelling' as const,
        cancellationError: undefined,
      }));
    case 'PRICE_IMPROVEMENT_CANCEL_RECEIVED':
    case 'PRICE_IMPROVEMENT_DISCARDED':
      return addOrReplaceKey(state, action.quoteId, <PriceImprovementRequest>{
        status: 'Initialized',
        expirationTime: 'ThreeMinutes',
        isExpanded: false,
      });
    case 'PRICE_IMPROVEMENT_CANCEL_FAILED':
      return patchKey(state, action.quoteId, _ => ({
        status: 'Created' as const,
        cancellationError: action.error,
      }));
    case 'PRICE_IMPROVEMENT_ERROR_DISMISSED':
      return patchKey(state, action.quoteId, _ => ({
        creationError: undefined,
        cancellationError: undefined,
      }));
    case 'PRICE_IMPROVEMENT_TRADER_ACKNOWLEDGED':
      return patchKeyIfExist(state, action.quoteId, _ => ({
        status: 'Acknowledged' as const,
      }));
    case 'PRICE_IMPROVEMENT_TRADER_REJECTED':
      return patchKeyIfExist(state, action.quoteId, _ => ({
        status: 'Rejected' as const,
        traderComment: action.comment,
      }));
    case 'PRICE_IMPROVEMENT_TRADER_VALIDATED':
      return patchKeyIfExist(state, action.quoteId, _ => ({
        status: 'Validated' as const,
        traderComment: action.comment,
        priceAdjustment: action.priceAdjustment,
      }));
    case 'UI_CONFIRMED_IS_TRADING':
      return patchKeyIfExist(state, action.quoteId, _ => ({
        isExpanded: false,
      }));
    case 'QUOTE_REMOVED':
      if (action.workspace !== 'static') {
        return state;
      }
      return removeKey(state, action.quoteId);
    default:
      return state;
  }
}
